import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client from "../api/client";

const WebsiteContext = createContext(null);

export function WebsiteProvider({ children }) {
  const [websites, setWebsites] = useState([]);
  const [activeWebsiteId, setActiveWebsiteId] = useState(localStorage.getItem("active_website_id") || "");
  const [loading, setLoading] = useState(false);

  const loadWebsites = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/websites");
      const list = data.data || [];
      setWebsites(list);

      const exists = list.some((w) => String(w.id) === String(activeWebsiteId));
      if (!exists) {
        setActiveWebsiteId("");
        localStorage.removeItem("active_website_id");
      }
    } catch (error) {
      console.error("Failed to load websites:", error);
    } finally {
      setLoading(false);
    }
  }, [activeWebsiteId]);

  useEffect(() => {
    loadWebsites().catch(() => {
      // Prevent unhandled rejection from bubbling during initial bootstrap.
    });
  }, [loadWebsites]);

  const setActiveWebsite = (id) => {
    const next = String(id || "");
    setActiveWebsiteId(next);
    if (next) {
      localStorage.setItem("active_website_id", next);
    } else {
      localStorage.removeItem("active_website_id");
    }
  };

  const createWebsite = async (payload) => {
    const { data } = await client.post("/websites", payload);
    await loadWebsites();
    if (data?.id) {
      const next = String(data.id);
      setActiveWebsiteId(next);
      localStorage.setItem("active_website_id", next);
    }
  };

  const activeWebsite = useMemo(
    () => websites.find((w) => String(w.id) === String(activeWebsiteId)) || null,
    [websites, activeWebsiteId]
  );

  const value = useMemo(
    () => ({
      websites,
      activeWebsiteId,
      activeWebsite,
      loading,
      loadWebsites,
      setActiveWebsite,
      createWebsite
    }),
    [websites, activeWebsiteId, activeWebsite, loading, loadWebsites]
  );

  return <WebsiteContext.Provider value={value}>{children}</WebsiteContext.Provider>;
}

export function useWebsite() {
  return useContext(WebsiteContext);
}
